function audio_context_ctor ()
  {
    if (typeof window === 'undefined') return null;
    return window.AudioContext || window.webkitAudioContext || null;
  }

function volume_normalise (pVolume)
  {
    const tVolume = Number (pVolume);
    if (!Number.isFinite (tVolume)) return 1;
    return Math.max (0, Math.min (1, tVolume));
  }

function decode_audio_data (pContext, pArrayBuffer)
  {
    return new Promise ((resolve, reject) =>
      {
        try
          {
            const tResult = pContext.decodeAudioData (pArrayBuffer.slice (0), resolve, reject);
            if (tResult && typeof tResult.then === 'function') tResult.then (resolve, reject);
          }
        catch (e)
          {
            reject (e);
          }
      });
  }

function play_html_audio (pUrl, pVolume)
  {
    return new Promise ((resolve) =>
      {
        const tAudio = new Audio (pUrl);
        let tDone = false;
        const termine = (pOk) =>
          {
            if (tDone) return;
            tDone = true;
            tAudio.removeEventListener ('ended', onEnded);
            tAudio.removeEventListener ('error', onError);
            resolve (!!pOk);
          };
        const onEnded = () => termine (true);
        const onError = () => termine (false);
        tAudio.preload = 'auto';
        tAudio.volume = volume_normalise (pVolume);
        tAudio.addEventListener ('ended', onEnded);
        tAudio.addEventListener ('error', onError);
        const tPlay = tAudio.play ();
        if (tPlay && typeof tPlay.catch === 'function')
          tPlay.catch (() => termine (false));
      });
  }

async function fetch_audio_array_buffer (pUrl, pOptions = {})
  {
    const tReponse = await fetch (
      pUrl,
      {
        credentials : 'same-origin',
        cache       : pOptions.cache || 'force-cache',
      }
    );
    if (!tReponse || !tReponse.ok) throw new Error ('audio-fetch-failed');
    return await tReponse.arrayBuffer ();
  }

async function load_audio_buffer (pContext, pUrl, pOptions = {})
  {
    const tArrayBuffer = await fetch_audio_array_buffer (pUrl, pOptions);
    return await decode_audio_data (pContext, tArrayBuffer);
  }

export function create_lecture_audio_player ()
  {
    let tContext = null;
    let tCurrent = null;
    let tUnlocked = false;
    const tBuffers = new Map ();

    function context ()
      {
        if (tContext) return tContext;
        const tCtor = audio_context_ctor ();
        if (!tCtor) return null;
        tContext = new tCtor ();
        return tContext;
      }

    async function resume ()
      {
        const tCtx = context ();
        if (!tCtx) return null;
        if (tCtx.state === 'suspended' && typeof tCtx.resume === 'function')
          await tCtx.resume ();
        return tCtx;
      }

    function unlock ()
      {
        const tCtx = context ();
        if (!tCtx) return false;
        if (tCtx.state === 'suspended' && typeof tCtx.resume === 'function')
          tCtx.resume ().catch (() => {});
        if (tUnlocked && tCtx.state === 'running') return true;
        try
          {
            const tBuffer = tCtx.createBuffer (1, 1, Math.max (8000, tCtx.sampleRate || 44100));
            const tSource = tCtx.createBufferSource ();
            const tGain = tCtx.createGain ();
            tGain.gain.value = 0;
            tSource.buffer = tBuffer;
            tSource.connect (tGain);
            tGain.connect (tCtx.destination);
            tSource.onended = () =>
              {
                try { tSource.disconnect (); } catch (e) {}
                try { tGain.disconnect (); } catch (e) {}
              };
            tSource.start (0);
            tUnlocked = true;
            return true;
          }
        catch (e)
          {
            return false;
          }
      }

    function buffer_entry (pUrl)
      {
        const tUrl = String (pUrl || '');
        if (!tUrl) return null;
        const tExistant = tBuffers.get (tUrl);
        if (tExistant && !tExistant.error) return tExistant;
        if (tExistant && tExistant.error) tBuffers.delete (tUrl);
        const tEntry = {buffer : null, promise : null, error : null};
        tEntry.promise = (async () =>
          {
            try
              {
                const tCtx = context ();
                if (!tCtx) throw new Error ('audio-context-unavailable');
                try
                  {
                    tEntry.buffer = await load_audio_buffer (tCtx, tUrl);
                  }
                catch (eCharge)
                  {
                    tEntry.buffer = await load_audio_buffer (tCtx, tUrl, {cache : 'reload'});
                  }
                return tEntry.buffer;
              }
            catch (e)
              {
                tEntry.error = e;
                throw e;
              }
          }) ();
        tBuffers.set (tUrl, tEntry);
        return tEntry;
      }

    function prepare (pUrl)
      {
        const tEntry = buffer_entry (pUrl);
        if (!tEntry) return null;
        tEntry.promise.catch (() => {});
        return tEntry.promise;
      }

    function stop ()
      {
        if (!tCurrent) return;
        const tStop = tCurrent;
        tCurrent = null;
        try { tStop.source.stop (0); } catch (e) {}
        try { tStop.source.disconnect (); } catch (e) {}
        try { tStop.gain.disconnect (); } catch (e) {}
        if (typeof tStop.resolve === 'function') tStop.resolve (false);
      }

    function setVolume (pVolume)
      {
        if (!tCurrent || !tCurrent.gain) return false;
        try
          {
            tCurrent.gain.gain.value = volume_normalise (pVolume);
            return true;
          }
        catch (e)
          {
            return false;
          }
      }

    async function play (pUrl, pOptions = {})
      {
        const tUrl = String (pUrl || '');
        if (!tUrl) return false;
        const tVolume = volume_normalise (pOptions.volume);
        stop ();
        try
          {
            unlock ();
            const tCtx = context ();
            if (!tCtx) return play_html_audio (tUrl, tVolume);
            if (tCtx.state === 'suspended' && typeof tCtx.resume === 'function')
              tCtx.resume ().catch (() => {});
            const tEntry = buffer_entry (tUrl);
            if (tEntry && !tEntry.buffer && tEntry.promise)
              {
                tEntry.promise.catch (() => {});
                return play_html_audio (tUrl, tVolume);
              }
            const tBuffer = tEntry && tEntry.buffer;
            if (!tBuffer) return play_html_audio (tUrl, tVolume);

            return await new Promise ((resolve) =>
              {
                const tSource = tCtx.createBufferSource ();
                const tGain = tCtx.createGain ();
                let tDone = false;
                const termine = (pOk) =>
                  {
                    if (tDone) return;
                    tDone = true;
                    if (tCurrent && tCurrent.source === tSource) tCurrent = null;
                    try { tSource.disconnect (); } catch (e) {}
                    try { tGain.disconnect (); } catch (e) {}
                    resolve (!!pOk);
                  };
                tSource.buffer = tBuffer;
                tGain.gain.value = tVolume;
                tSource.connect (tGain);
                tGain.connect (tCtx.destination);
                tSource.onended = () => termine (true);
                tCurrent = {source : tSource, gain : tGain, resolve : termine};
                tSource.start (0);
              });
          }
        catch (e)
          {
            return play_html_audio (tUrl, tVolume);
          }
      }

    function dispose ()
      {
        stop ();
        tBuffers.clear ();
        if (tContext && typeof tContext.close === 'function')
          tContext.close ().catch (() => {});
        tContext = null;
        tUnlocked = false;
      }

    return {prepare, play, stop, setVolume, dispose, unlock};
  }
